import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { PapInput, PapTextArea, PapSelect, PapErrors } from "../FormElements";
import { X, CalendarPlus, Save, Palette } from "lucide-react";

interface CalendarEvent {
  id?: number;
  title: string;
  description?: string | null;
  start_time: string;
  end_time: string;
  location?: string;
  color?: string;
  child_id?: string | null;
  child_name?: string | null;
}

interface EventFormModalProps {
  event: CalendarEvent | null;
  children: { id: string; first_name: string }[];
  onSave: (data: Record<string, any>) => Promise<void>;
  onClose: () => void;
}

const schema = yup.object().shape({
  title: yup.string().required("Title is required").label("Title"),
  description: yup.string().optional().nullable().label("Description"),
  start_time: yup.string().required("Start time is required").label("Start Time"),
  end_time: yup.string().required("End time is required").label("End Time"),
  location: yup.string().optional().label("Location"),
  color: yup.string().optional().label("Color"),
  child_id: yup.string().optional().nullable().label("Child"),
});

const DEFAULT_EVENT_COLOR = "#4f46e5";

const COLOR_OPTIONS = [
  { id: DEFAULT_EVENT_COLOR, name: "Indigo", hex: DEFAULT_EVENT_COLOR },
  { id: "#16a34a", name: "Green", hex: "#16a34a" },
  { id: "#dc2626", name: "Red", hex: "#dc2626" },
  { id: "#ea580c", name: "Orange", hex: "#ea580c" },
  { id: "#0891b2", name: "Cyan", hex: "#0891b2" },
  { id: "#7c3aed", name: "Purple", hex: "#7c3aed" },
];

function toLocalDatetimeString(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const EventFormModal: React.FC<EventFormModalProps> = ({ event, children: childrenList, onSave, onClose }) => {
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [selectedColor, setSelectedColor] = useState(event?.color || DEFAULT_EVENT_COLOR);
  const isEditing = !!event?.id;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (event) {
      reset({
        title: event.title || "",
        description: event.description || "",
        start_time: event.start_time ? toLocalDatetimeString(event.start_time) : "",
        end_time: event.end_time ? toLocalDatetimeString(event.end_time) : "",
        location: event.location || "",
        color: event.color || DEFAULT_EVENT_COLOR,
        child_id: event.child_id || "",
      });
      setSelectedColor(event.color || DEFAULT_EVENT_COLOR);
    } else {
      const now = new Date();
      const oneHourLater = new Date(now.getTime() + 3600000);
      reset({
        title: "",
        description: "",
        start_time: toLocalDatetimeString(now.toISOString()),
        end_time: toLocalDatetimeString(oneHourLater.toISOString()),
        location: "",
        color: DEFAULT_EVENT_COLOR,
        child_id: "",
      });
      setSelectedColor(DEFAULT_EVENT_COLOR);
    }
  }, [event, reset]);

  const onSubmit = async (data: Record<string, any>) => {
    setSaving(true);
    setFormErrors([]);
    try {
      const payload = {
        ...data,
        start_time: new Date(data.start_time).toISOString(),
        end_time: new Date(data.end_time).toISOString(),
        child_id: data.child_id || null,
        child_name: data.child_id
          ? childrenList.find((c) => c.id === data.child_id)?.first_name || null
          : null,
      };
      await onSave(payload);
    } catch {
      setFormErrors(["Failed to save event. Please try again."]);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
        className="bg-card rounded-2xl shadow-soft-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto border border-border/80"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Color accent bar */}
        <div className="h-1.5 rounded-t-2xl transition-colors duration-300" style={{ backgroundColor: selectedColor }} />

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/60">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center transition-colors duration-300"
              style={{ backgroundColor: `${selectedColor}15` }}
            >
              {isEditing ? (
                <Save className="w-4.5 h-4.5" style={{ color: selectedColor }} />
              ) : (
                <CalendarPlus className="w-4.5 h-4.5" style={{ color: selectedColor }} />
              )}
            </div>
            <div>
              <h2 className="font-bold text-base m-0 text-card-foreground">
                {isEditing ? "Edit Event" : "New Event"}
              </h2>
              <p className="text-2xs text-muted-foreground m-0 mt-0.5">
                {isEditing ? "Update event details" : "Add to your calendar"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all active:scale-90"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form body */}
        <div className="px-6 py-5">
          <PapErrors errors={formErrors} />
          <form onSubmit={handleSubmit(onSubmit)}>
            <PapInput
              formBinding={register("title")}
              required={true}
              fieldLabel="Title"
              fieldName="title"
              error={errors.title}
            />
            <PapTextArea
              formBinding={register("description")}
              fieldLabel="Description"
              fieldName="description"
              placeholder="Add a description..."
              error={errors.description}
            />

            {/* Date/time fields in a grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
              <div className="mb-5">
                <label className="form-label" htmlFor="start_time">
                  Start Time<span className="text-destructive ml-1">*</span>
                </label>
                <input
                  {...register("start_time")}
                  type="datetime-local"
                  id="start_time"
                  className={`form-input ${errors.start_time ? "border-destructive ring-1 ring-destructive/20" : ""}`}
                />
                {errors.start_time && (
                  <p className="mt-1.5 text-xs text-destructive font-medium">{errors.start_time.message}</p>
                )}
              </div>

              <div className="mb-5">
                <label className="form-label" htmlFor="end_time">
                  End Time<span className="text-destructive ml-1">*</span>
                </label>
                <input
                  {...register("end_time")}
                  type="datetime-local"
                  id="end_time"
                  className={`form-input ${errors.end_time ? "border-destructive ring-1 ring-destructive/20" : ""}`}
                />
                {errors.end_time && (
                  <p className="mt-1.5 text-xs text-destructive font-medium">{errors.end_time.message}</p>
                )}
              </div>
            </div>

            <PapInput
              formBinding={register("location")}
              required={false}
              fieldLabel="Location"
              fieldName="location"
              error={errors.location}
            />

            {/* Color picker - visual swatches */}
            <div className="mb-5">
              <label className="form-label flex items-center gap-1.5">
                <Palette className="w-3.5 h-3.5 text-muted-foreground" />
                Color
              </label>
              <div className="flex items-center gap-2">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color.id}
                    type="button"
                    onClick={() => {
                      setSelectedColor(color.hex);
                      setValue("color", color.id);
                    }}
                    className={`w-8 h-8 rounded-xl transition-all duration-200 hover:scale-110 active:scale-95 ${
                      selectedColor === color.hex
                        ? 'ring-2 ring-offset-2 ring-offset-card scale-110 shadow-soft-sm'
                        : 'hover:shadow-soft-sm'
                    }`}
                    style={{
                      backgroundColor: color.hex,
                    }}
                    title={color.name}
                  />
                ))}
              </div>
              <input type="hidden" {...register("color")} />
            </div>

            <PapSelect
              formBinding={register("child_id")}
              options={childrenList.map((c) => ({ id: c.id, name: c.first_name }))}
              placeholder="No child (family event)"
              fieldName="child_id"
              fieldLabel="Child"
              required={false}
              error={errors.child_id}
            />

            {/* Action buttons */}
            <div className="flex gap-3 pt-3 border-t border-border/40 mt-2">
              <button
                type="submit"
                className="btn btn-primary flex-1"
                disabled={saving}
                style={{
                  backgroundColor: saving ? undefined : selectedColor,
                }}
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : isEditing ? (
                  <span className="flex items-center gap-1.5">
                    <Save className="w-4 h-4" />
                    Update Event
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <CalendarPlus className="w-4 h-4" />
                    Create Event
                  </span>
                )}
              </button>
              <button type="button" className="btn btn-ghost px-5" onClick={onClose}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default EventFormModal;
